import type { ExpandParams, GraphicsParams, ModifyParams, RetouchParams } from './components/ImageEditorModal';

/**
 * An image edit as the API runs it: which recipe, with which inputs, chained
 * from the picture being edited.
 *
 * Shared by the canvas (edit-image nodes) and the Studio (editing a shot), so
 * the two never disagree about what an edit sends — the retouch regions as a
 * JSON string, the graphics source size, and so on.
 */

export type EditTool = 'expand' | 'retouch' | 'modify' | 'graphics' | 'upscale';

export interface EditParams {
    expand?: ExpandParams;
    retouch?: RetouchParams;
    modify?: ModifyParams;
    graphics?: GraphicsParams;
    upscaleScale?: number;
}

export interface EditRequest {
    recipeSlug: string;
    inputs: Record<string, any>;
}

/** The recipe and inputs for an edit, or null when it has nothing to do yet. */
export function editImageRequest(
    tool: EditTool,
    params: EditParams,
    source: { width?: number; height?: number } = {},
): EditRequest | null {
    if (tool === 'expand') {
        const e = params.expand;
        if (!e) return null;
        return {
            recipeSlug: 'expand-image',
            inputs: { extendTop: e.extendTop, extendRight: e.extendRight, extendBottom: e.extendBottom, extendLeft: e.extendLeft, prompt: e.prompt },
        };
    }
    if (tool === 'retouch') {
        const ready = (params.retouch?.regions || []).filter((r) => r.prompt.trim() && r.width > 0 && r.height > 0);
        if (ready.length === 0) return null;
        return {
            recipeSlug: 'retouch-image',
            inputs: {
                // The lasso polygon; the bbox rides along for placement and
                // the worker's legacy rectangle fallback.
                regions: JSON.stringify(ready.map((r) => ({
                    points: r.points, x: r.x, y: r.y, width: r.width, height: r.height, prompt: r.prompt.trim(),
                }))),
            },
        };
    }
    if (tool === 'modify') {
        const p = (params.modify?.prompt || '').trim();
        if (!p) return null;
        return { recipeSlug: 'modify-image', inputs: { prompt: p, referenceUrl: params.modify?.referenceUrl || undefined } };
    }
    if (tool === 'graphics') {
        const g = params.graphics;
        if (!g) return null;
        const texts = g.textAnnotations.filter((a) => a.text.trim().length > 0);
        if (texts.length === 0 && g.arrows.length === 0 && !(g.referenceUrl && g.prompt.trim())) return null;
        return {
            recipeSlug: 'graphics-image',
            inputs: {
                // Structured inputs travel as strings, like retouch regions.
                textAnnotations: JSON.stringify(texts.map((a) => ({ text: a.text.trim(), x: a.x, y: a.y, fontSize: a.fontSize, weight: a.weight }))),
                arrows: JSON.stringify(g.arrows),
                prompt: g.prompt.trim(),
                referenceUrl: g.referenceUrl || undefined,
                sourceWidth: source.width,
                sourceHeight: source.height,
            },
        };
    }
    if (tool === 'upscale') {
        return { recipeSlug: 'upscale-image', inputs: { scale: params.upscaleScale || 2 } };
    }
    return null;
}
